import {
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ApplicationRoutes } from '../../../../application/constants/routes';
import { useI18n } from '../../../../application/language/useI18n';
import { ILearningUserRatingModel } from '../../../../service/report/interface/ILearningUserRatingModel';
import { getPersistedRatingsState, setPersistedRatingsState } from './persistence';
import { RatingProgress } from './RatingProgress';
import { renderTeamsCell } from './renderTeamsCell';

type IUserRatingsSortField =
  | 'userName'
  | 'projectRoles'
  | 'progress'
  | 'score';
type IOrder = 'asc' | 'desc';
type IUserRatingsTableState = {
  order: IOrder;
  orderBy: IUserRatingsSortField;
  page: number;
  rowsPerPage: number;
};

const USER_RATINGS_TABLE_STATE_KEY = 'analytics/ratings/users/table';

type IUserRatingsTableProperties = {
  rows: ILearningUserRatingModel[];
};

const sortRows = (rows: ILearningUserRatingModel[], orderBy: IUserRatingsSortField, order: IOrder) => {
  return [...rows].sort((a, b) => {
    const direction = order === 'asc' ? 1 : -1;

    switch (orderBy) {
      case 'userName':
        return a.userName.localeCompare(b.userName) * direction;
      case 'projectRoles':
        return a.projectRoles.join(', ').localeCompare(b.projectRoles.join(', ')) * direction;
      case 'progress':
        return (a.progress - b.progress) * direction;
      case 'score':
        return (a.score - b.score) * direction;
      default:
        return 0;
    }
  });
};

export const UserRatingsTable = ({ rows }: IUserRatingsTableProperties) => {
  const { translate } = useI18n();
  const persistedState = getPersistedRatingsState<IUserRatingsTableState>(USER_RATINGS_TABLE_STATE_KEY, {
    order: 'desc',
    orderBy: 'progress',
    page: 0,
    rowsPerPage: 10,
  });
  const [order, setOrder] = useState<IOrder>(persistedState.order);
  const [orderBy, setOrderBy] = useState<IUserRatingsSortField>(persistedState.orderBy);
  const [page, setPage] = useState(persistedState.page);
  const [rowsPerPage, setRowsPerPage] = useState(persistedState.rowsPerPage);

  useEffect(() => {
    setPersistedRatingsState(USER_RATINGS_TABLE_STATE_KEY, {
      order,
      orderBy,
      page,
      rowsPerPage,
    });
  }, [order, orderBy, page, rowsPerPage]);

  const handleSort = (field: IUserRatingsSortField) => {
    setOrder((prev) => (orderBy === field && prev === 'asc' ? 'desc' : 'asc'));
    setOrderBy(field);
  };

  const sortedRows = sortRows(rows, orderBy, order);
  const safePage = page * rowsPerPage >= sortedRows.length && page > 0 ? 0 : page;
  const visibleRows = sortedRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
  const columnCount = 6;

  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight: 520, overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }}>№</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'userName'}
                  direction={orderBy === 'userName' ? order : 'asc'}
                  onClick={() => handleSort('userName')}
                >
                  {translate('analytics.ratings.users.userName')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'projectRoles'}
                  direction={orderBy === 'projectRoles' ? order : 'asc'}
                  onClick={() => handleSort('projectRoles')}
                >
                  {translate('analytics.ratings.users.projectRoles')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{translate('analytics.ratings.users.projects')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'progress'}
                  direction={orderBy === 'progress' ? order : 'asc'}
                  onClick={() => handleSort('progress')}
                >
                  {translate('analytics.ratings.users.progress')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'score'}
                  direction={orderBy === 'score' ? order : 'asc'}
                  onClick={() => handleSort('score')}
                >
                  {translate('analytics.ratings.users.score')}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={row.userId}>
                <TableCell>{safePage * rowsPerPage + index + 1}</TableCell>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={ApplicationRoutes.AnalyticsUser.replace(':userId', row.userId)}
                    underline="hover"
                    color="inherit"
                  >
                    {row.userName}
                  </Link>
                </TableCell>
                <TableCell>{row.projectRoles.join(', ')}</TableCell>
                <TableCell>
                  {renderTeamsCell(
                    row.projects.map((project) => ({
                      id: project.id,
                      name: project.name,
                      to: ApplicationRoutes.AnalyticsProject.replace(':projectId', project.id),
                    })),
                  )}
                </TableCell>
                <TableCell>
                  <RatingProgress value={row.progress} label={`${row.progress}% (${row.passedUnitsCount}/${row.totalUnitsCount})`} threeColors />
                </TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{`${row.score} / ${row.maxScore}`}</TableCell>
              </TableRow>
            ))}
            {!visibleRows.length && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  {translate('analytics.ratings.noData')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={safePage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25]}
        labelRowsPerPage={translate('analytics.ratings.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${translate('analytics.ratings.of')} ${count}`}
        getItemAriaLabel={(type) =>
          type === 'next' ? translate('analytics.ratings.nextPage') : translate('analytics.ratings.previousPage')
        }
      />
    </>
  );
};
